import React, { FC } from 'react';
import { connect } from 'react-redux';
import { compose } from '@reduxjs/toolkit';

import type { ReduxState } from 'app/redux/types';

import { withinBettingContext } from 'app/contexts/betting';
import { useTranslation } from 'app/lang';
import { getTruncatedNumber } from 'app/lib/formatters';

import { Stake, StakeBlank } from '../../../StakeRepresentation';

import dictionary from './RightCellHeadToHead.dictionary.json';

import matchRowStyles from '../MatchRow.module.scss';
import styles from './RightCellHeadToHead.module.scss';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type BettingContextProps = any;

type StoreProps = {
  lineId: number;
  visibleStakeIds : number[];
};

type RightCellHeadToHeadProps = {
  matchId: number;
  betCount: number;
  onClick: VoidFunction;
};

const RightCellHeadToHead: FC<RightCellHeadToHeadProps & StoreProps> = ({
  visibleStakeIds,
  matchId,
  lineId,
  betCount,
  onClick,
}) => {
  const { t } = useTranslation();

  if (!betCount) {
    return (
      <div className={styles.contentWithoutBets}>
        {t(dictionary.noBets)}
      </div>
    );
  }

  const notVisibleStake = visibleStakeIds?.every((stakeId: number) => !stakeId);

  if (notVisibleStake) {
    return (
      <button type="button"
              onClick={onClick}
              className={styles.contentWithMainBets}>
        <span className={styles.expansion}>
          <p className={styles.expansionText}>
            {t(dictionary.lookAtOtherBets)}
          </p>
          ({`+${getTruncatedNumber(betCount, { accuracy: { thousands: 1 } })}`})
        </span>
      </button>
    );
  }

  return (
    <div data-test="match-row-header-bets"
         className={styles.rightCellHeadToHead}>
      {visibleStakeIds !== null && visibleStakeIds.map((stakeId: number, index: number) => {
        if (!stakeId) {
          return (
            <StakeBlank className={matchRowStyles.mainLineStakes}
                        key={`${index}/${stakeId}`} />
          );
        }

        return (
          <Stake key={`${index}/${stakeId}`}
                 lineId={lineId}
                 matchId={matchId}
                 stakeId={stakeId}
                 className={matchRowStyles.mainLineStakes} />
        );
      })}
    </div>
  );
};

const createMapStateToProps = (state: ReduxState, {
  $betting: {
    grid: {
      selectors: {
        mainLineId,
        visibleLineStakeIdsSelector,
      },
    },
  },
  matchId,
}: BettingContextProps) => {
  const lineId = mainLineId(state, { matchId });

  return {
    lineId,
    visibleStakeIds: lineId === null
      ? null
      : visibleLineStakeIdsSelector(state, lineId, matchId),
  };
};

export default compose<FC<RightCellHeadToHeadProps>>(
  withinBettingContext,
  connect(createMapStateToProps),
)(RightCellHeadToHead);
